import React from 'react';
import { FieldValues, UseFormTrigger } from 'react-hook-form';

import classNames from 'classnames';
import countyCodesList from 'country-codes-list';
import countries, { Alpha2Code } from 'i18n-iso-countries';
import daLocale from 'i18n-iso-countries/langs/da.json';
import deLocale from 'i18n-iso-countries/langs/de.json';
import enLocale from 'i18n-iso-countries/langs/en.json';
import esLocale from 'i18n-iso-countries/langs/es.json';
import fiLocale from 'i18n-iso-countries/langs/fi.json';
import frLocale from 'i18n-iso-countries/langs/fr.json';
import itLocale from 'i18n-iso-countries/langs/it.json';
import noLocale from 'i18n-iso-countries/langs/nb.json';
import nlLocale from 'i18n-iso-countries/langs/nl.json';
import plLocale from 'i18n-iso-countries/langs/pl.json';
import ptLocale from 'i18n-iso-countries/langs/pt.json';
import svLocale from 'i18n-iso-countries/langs/sv.json';

import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { GlobalMessages } from '../../types/Messages';
import { CountryCode, Language } from '../../types/apiEnums';
import { languageUtils } from '../../utils/languageUtils/languageUtils';
import { FORM_INPUT_PHONE_NUMBER } from '../FormInputPhoneNumber/FormInputPhoneNumber';
import { Select } from '../Select/Select';
import styles from './FormSelectPhoneCodeCountry.module.css';

countries.registerLocale({
  locale: Language.de,
  countries: deLocale.countries,
});

countries.registerLocale({
  locale: Language.en,
  countries: enLocale.countries,
});

countries.registerLocale({
  locale: Language.es,
  countries: esLocale.countries,
});

countries.registerLocale({
  locale: Language.fr,
  countries: frLocale.countries,
});

countries.registerLocale({
  locale: Language.it,
  countries: itLocale.countries,
});

countries.registerLocale({
  locale: Language.nl,
  countries: nlLocale.countries,
});

countries.registerLocale({
  locale: Language.pl,
  countries: plLocale.countries,
});

countries.registerLocale({
  locale: Language.sv,
  countries: svLocale.countries,
});

countries.registerLocale({
  locale: Language.nb,
  countries: noLocale.countries,
});

countries.registerLocale({
  locale: Language.da,
  countries: daLocale.countries,
});

countries.registerLocale({
  locale: Language.pt,
  countries: ptLocale.countries,
});

countries.registerLocale({
  locale: Language.fi,
  countries: fiLocale.countries,
});

export const phoneCodesList: PhoneCodesList =
  countyCodesList.customList('countryCode', '+{countryCallingCode}');

export type PhoneCodesList = {
  [key: string]: string;
};

export const hooks = {
  useCountriesList(countryCodes?: Alpha2Code[]) {
    const language = messageHooks.useSubjectState(GlobalMessages.Language);
    const defaultLanguage = languageUtils.getDefaultLanguage();
    const formattedLang = language ? language : defaultLanguage;

    return React.useMemo(() => {
      const list = [];
      if (countryCodes && countryCodes.length) {
        const items = countryCodes.map((code) => ({
          id: code,
          label: countries.getName(code, formattedLang),
        }));
        list.push(...items);
      } else {
        const countriesNames = countries.getNames(formattedLang);
        for (const alpha2Key in countriesNames) {
          list.push({ id: alpha2Key, label: countriesNames[alpha2Key] });
        }
      }
      return list.sort((a, b) => a.label.localeCompare(b.label));
    }, [formattedLang, countryCodes]);
  },
  useDropdownState(
    onChange: (countryCode: string, phoneCode: string) => void,
    trigger: UseFormTrigger<FieldValues>,
    isFormDirty: boolean,
    defaultValue?: CountryCode | null,
  ) {
    const defaultCountryCode = languageUtils.getCountryCode() as CountryCode;

    const [selectedCountryId, setSelectedCountryId] =
      React.useState<CountryCode>(defaultValue
        ? defaultValue
        : defaultCountryCode);

    React.useEffect(() => {
      defaultValue && setSelectedCountryId(defaultValue);

      !defaultValue && onChange(selectedCountryId, phoneCodesList[selectedCountryId]);

      isFormDirty && trigger(FORM_INPUT_PHONE_NUMBER);
    }, [defaultValue, isFormDirty, onChange, selectedCountryId, trigger]);

    const handleCountryChange = React.useCallback(
      (countryCode: CountryCode) => {
        setSelectedCountryId(countryCode);

        onChange(countryCode, phoneCodesList[countryCode]);
      },
      [onChange],
    );

    return {
      selectedCountryId,
      onSelected: handleCountryChange,
    };
  },
};

export type Props<T extends FieldValues,> = {
  countryCodes?: Alpha2Code[];
  defaultValue?: CountryCode | null;
  isFormDirty: boolean;
  onChange: (countryCode: Alpha2Code, phoneCode: string) => void;
  selectHeaderClassName?: string;
  trigger: UseFormTrigger<T>;
};
export const FormSelectPhoneCodeCountry = React.memo(<T extends FieldValues,>(props: Props<T>) => {
  const {
    trigger,
    onChange,
    isFormDirty,
    defaultValue,
    selectHeaderClassName,
  } = props;

  const { selectedCountryId, onSelected } = hooks.useDropdownState(
    onChange,
    trigger as UseFormTrigger<FieldValues>,
    isFormDirty,
    defaultValue,
  );

  const refCountry = React.useRef<HTMLDivElement>(null);

  const countriesList = hooks.useCountriesList(props.countryCodes);

  return (
    <Select
      onChange={onSelected}
      items={countriesList}
      defaultSelectedItemId={selectedCountryId || undefined}
      search
      ref={refCountry}
      customClasses={{
        selectHeader: classNames(styles.selectCountryHeader, selectHeaderClassName),
        selectList: styles.selectCountryList,
      }}
    />
  );
});

FormSelectPhoneCodeCountry.displayName = 'FormSelectPhoneCodeCountry';
